/**
 * `fileId` is encoded because it is a **file name**, not an opaque id.
 *
 * The API derives `file_id` from the S3 path by taking the last segment, so
 *   what arrives here is whatever the document was called. Measured across the
 *   60,315 objects under `cases/` in production: 58.3% contain a space, 110 a
 *   quote or backslash, 82 are non-ASCII, 27 contain `+` and 2 contain `#`.
 *
 * Interpolated raw, `fetch` percent-encodes most of that on its own — a space
 *   already leaves as `%20`, and so do quotes and non-ASCII, which is why the
 *   58% are fine today and this is not a fix for them. Three classes are not
 *   fine:
 *
 *   - `#` starts a URL fragment, so the name is **truncated** and the request
 *     asks for a different file. Two documents are unreachable today, and the
 *     symptom is a click that does nothing.
 *   - `+` needs nothing, and is listed only because it looks like it should.
 *     Sinatra 4 unescapes route captures with `URI::Parser#unescape`, which
 *     decodes `%XX` and leaves `+` alone, so the 27 names containing one work
 *     both before and after this change.
 *   - a literal `%20` inside a name would be sent unchanged and decoded to a
 *     space, fetching the wrong file. None exist today.
 *
 * `encodeURIComponent` makes all three deterministic and leaves the rest
 *   byte-identical. It is safe to encode the whole value because no name
 *   contains a slash — verified by path depth in the bucket: 60,260 objects at
 *   `cases/<id>/<name>` and none deeper — so no path separator is swallowed.
 */
export const getFetchAppFileEndpoint = ({
  caseId,
  fileId,
}: {
  caseId: string;
  fileId: string;
}) => `cases/${caseId}/attachments/${encodeURIComponent(fileId)}`;

export const getFetchAppFilesEndpoint = ({ caseId }: { caseId: string }) =>
  `cases/${caseId}/attachments`;

export const getUploadAppFileEndpoint = getFetchAppFilesEndpoint;

/**
 * How long to keep re-reading the attachment list after a signature.
 *
 * The archived document does not exist when the embed reports a signature. The
 *   backend measured the gap in production on both HelloSign documents it had:
 *   **16.5 s and 14.7 s** from the patient's click to the `case_files3` row.
 *   Most of that is the provider — their own callback-to-row segment is ~4 s.
 *
 * So a single refetch at the moment of signing, which is what this app did,
 *   missed the row every time rather than occasionally. `POLL_MAX_ATTEMPTS`
 *   covers 30 s, roughly double the measured worst case.
 *
 * Not finding the row is a normal intermediate state here, not an absence: the
 *   caller must not treat a poll that has not resolved yet as "no document".
 */
export const CASE_FILES_POLL_INTERVAL_MS = 2500;
export const CASE_FILES_POLL_MAX_ATTEMPTS = 12;

/**
 * Hard stop, however slow the reads are.
 *
 * The attempts are spaced by the interval *plus* however long each read takes,
 *   so the run stretches on a slow connection rather than dropping attempts.
 *   That is the right trade — an attempt that happens late still answers, and
 *   one that was cancelled answers never — but it means the nominal 30 s is a
 *   floor and not a bound. This is the bound, and it also catches the case the
 *   attempt counter cannot: a read that never resolves at all, which would
 *   otherwise leave the timer running with every tick skipped.
 */
export const CASE_FILES_POLL_DEADLINE_MS = 90_000;
